import { useEffect, useId, useMemo, useState } from 'react'
import { vendorUpdateSubtypes } from '@intelhub/shared/ontology'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import {
  useSaveVendorContent,
  useUserPreferences,
  useVendorsByIds,
  VENDOR_SEARCH_MAX_LENGTH,
  VENDOR_SEARCH_PAGE,
  VENDOR_SUBJECT_MAX,
} from '@/hooks/queries'
import { useVendorPickerSearch } from '@/hooks/use-vendor-picker-search'
import type { Vendor, VendorContentPreference } from '@/lib/types'

/**
 * KAN-822 T9 (reader side) — the reader's saved Vendor updates preference
 * (spec R11 default off, R12 narrowing, R13 no subscription side effects,
 * Q1 narrowing matches the story's SUBJECT vendor, not its publisher).
 *
 * The form starts from the server's saved value and sends nothing until Save.
 * A save sends ONLY the changed fields (the API's partial update), so turning
 * it off keeps the narrowing and narrowing alone never opts in.
 *
 * Subject vendors (KAN-822 P2): the saved selection is resolved BY ID
 * (useVendorsByIds), so every saved vendor is shown however it sorts in the
 * directory; vendors to add come from a server-side search (useVendorSearch),
 * so the whole directory is reachable, not just its first page of 50 names.
 * Chosen vendors (saved, or ticked from a search) stay listed above the
 * search results whatever the search shows. A saved id the lookup does not
 * return has no vendor row any more: it is shown as a count and dropped only
 * when the reader saves a new vendor selection (the API refuses unknown ids).
 * Until the saved selection has resolved, vendor narrowing is not editable,
 * so nothing is dropped by accident. The API accepts at most
 * VENDOR_SUBJECT_MAX subject vendors: at the limit, unticked vendors cannot be
 * ticked (removing still works) and the reader is told why (the message also
 * describes each blocked checkbox). Search results are offered only for the
 * filter as typed, never an earlier query's page. Search and limit feedback
 * sits in regions that stay mounted (a polite status, and an alert for a
 * failure) so screen readers announce it.
 *
 * Vendor ticks are held as the reader's EDITS against the saved selection
 * (vendor id -> ticked), and the ticked set is derived during render from the
 * saved selection, those edits and the lookup. A lookup refetch therefore
 * re-derives the view without touching unsaved ticks or unticks; only a
 * change in the SAVED value (it loads, or a save is refetched) discards the
 * edits. An untick of a saved vendor stays a change even if the lookup then
 * stops returning it, so Save still removes it.
 *
 * Copy has had the humaniser pass (KAN-822 T9 reader-side PR notes). No dynamic
 * attribute carries data (replay masking): names are text nodes tied to their
 * controls with label[for].
 *
 * Rendered only by SettingsPage behind the Vendor UI build flag (dark).
 */
const SUBTYPES = [...vendorUpdateSubtypes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
const OFF: VendorContentPreference = { enabled: false, subjectVendorIds: [], subtypes: [] }

const sameSet = (a: Iterable<string>, b: Iterable<string>) => {
  const x = new Set(a)
  const y = new Set(b)
  return x.size === y.size && [...x].every((v) => y.has(v))
}

const byName = (a: Vendor, b: Vendor) => a.name.localeCompare(b.name)

function outcome(error: Error | null, isSuccess: boolean): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'Your settings record does not exist yet, so nothing was saved. Sign out and back in, then try again.'
  }
  if (error) return `Could not save: ${error.message}`
  return isSuccess ? 'Saved.' : ''
}

export function VendorPreferencesSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const prefsQuery = useUserPreferences(userId)
  const preferences = prefsQuery.data
  // Nothing is editable or savable until the saved preference has loaded:
  // a draft built from the "off" placeholder would overwrite the real value.
  const ready = prefsQuery.isSuccess === true
  const uid = useId()
  const save = useSaveVendorContent()

  const saved = preferences?.vendorContent ?? OFF
  const savedLookup = useVendorsByIds(saved.subjectVendorIds)
  const savedVendors = useMemo(
    () => (savedLookup.isSuccess && Array.isArray(savedLookup.data) ? savedLookup.data : null),
    [savedLookup.isSuccess, savedLookup.data],
  )
  const resolved = useMemo(() => new Map((savedVendors ?? []).map((v) => [v.id, v])), [savedVendors])
  const savedListedVendors = useMemo(
    () => saved.subjectVendorIds.filter((id) => resolved.has(id)),
    [saved.subjectVendorIds, resolved],
  )
  const dangling = savedVendors ? saved.subjectVendorIds.length - savedListedVendors.length : 0

  const [enabled, setEnabled] = useState(saved.enabled)
  const [subtypes, setSubtypes] = useState<Set<string>>(() => new Set(saved.subtypes))
  // The reader's unsaved vendor ticks: id -> ticked. An entry exists only
  // while it differs from the saved selection.
  const [vendorEdits, setVendorEdits] = useState<ReadonlyMap<string, boolean>>(() => new Map())
  // Vendors the reader ticked from a search, kept so they stay listed (and
  // sendable) after the search moves on.
  const [picked, setPicked] = useState<ReadonlyMap<string, Vendor>>(() => new Map())
  const [filter, setFilter] = useState('')
  const search = useVendorPickerSearch(filter)

  // Re-sync the draft from the server's saved value only when THAT changes
  // (it loads, or a save is refetched), keyed on its serialised value rather
  // than object identity. The vendor lookups are not inputs here: the vendor
  // selection below is derived from them, so a refetch never resets unsaved
  // edits.
  const savedKey = JSON.stringify(saved)
  useEffect(() => {
    setEnabled(saved.enabled)
    setSubtypes(new Set(saved.subtypes))
    setVendorEdits(new Map())
    setPicked(new Map())
  }, [savedKey])

  // A vendor can be shown and sent when the saved lookup returned it or the
  // reader ticked it from a search.
  const known = useMemo(() => {
    const m = new Map(resolved)
    for (const [id, v] of picked) if (!m.has(id)) m.set(id, v)
    return m
  }, [resolved, picked])

  // Ticked = saved selection with the reader's edits applied, limited to
  // vendors that can be shown (an unknown id cannot be shown or sent).
  const vendorIds = useMemo(() => {
    const ticked = new Set(savedListedVendors)
    for (const [id, on] of vendorEdits) {
      if (!known.has(id)) continue
      if (on) ticked.add(id)
      else ticked.delete(id)
    }
    return ticked
  }, [savedListedVendors, vendorEdits, known])
  const atLimit = vendorIds.size >= VENDOR_SUBJECT_MAX
  const limitId = `${uid}-limit`
  const toggleVendor = (vendor: Vendor) => {
    const id = vendor.id
    // At the API's cap only removals are allowed.
    if (atLimit && !vendorIds.has(id)) return
    if (!resolved.has(id)) setPicked((p) => (p.has(id) ? p : new Map(p).set(id, vendor)))
    setVendorEdits((edits) => {
      // From the edits, not the rendered set, so two toggles queued in one
      // render each see the other.
      const wasSaved = savedListedVendors.includes(id)
      const on = !(edits.get(id) ?? wasSaved)
      const next = new Map(edits)
      if (on === wasSaved) next.delete(id)
      else next.set(id, on)
      return next
    })
  }

  const changes: Partial<VendorContentPreference> = {}
  if (enabled !== saved.enabled) changes.enabled = enabled
  if (!sameSet(subtypes, saved.subtypes)) changes.subtypes = SUBTYPES.map((e) => e.slug).filter((s) => subtypes.has(s))
  // An untick of a SAVED vendor is a change even after the lookup stops
  // returning that vendor: the rendered set no longer differs from the saved
  // listed set, but the persisted tick is only removed by saving the selection.
  const unticksUnlistedSaved = [...vendorEdits].some(
    ([id, on]) => !on && !known.has(id) && saved.subjectVendorIds.includes(id),
  )
  if (savedVendors && (unticksUnlistedSaved || !sameSet(vendorIds, savedListedVendors))) {
    // Saved order first, then new ticks in the order they were made.
    const added = [...vendorEdits].filter(([id, on]) => on && !savedListedVendors.includes(id)).map(([id]) => id)
    changes.subjectVendorIds = [...savedListedVendors, ...added].filter((id) => vendorIds.has(id))
  }
  const dirty = Object.keys(changes).length > 0

  // Chosen vendors (saved or picked) are always listed, by name; search
  // results follow, without repeating a chosen vendor.
  const chosen = useMemo(() => [...known.values()].sort(byName), [known])
  const results = useMemo(() => search.results.filter((v) => !known.has(v.id)), [search.results, known])
  const resultCount = search.results.length
  const listed = [...chosen, ...results]

  const toggle = (set: Set<string>, value: string) => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    return next
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Vendor updates are product announcements, company news and promotional reports from security vendors. They
        stay out of your stories feed unless you turn them on here. Search still finds them, and they are always
        labelled.
      </p>

      {!ready && <p className="text-xs italic text-muted-foreground">Loading your settings…</p>}

      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium">Show vendor updates in my feed</p>
          <p className="text-xs text-muted-foreground">Briefings and alerts do not include them either way.</p>
        </div>
        <Switch
          checked={enabled}
          disabled={!ready}
          onCheckedChange={setEnabled}
          aria-label="Show vendor updates in my feed"
        />
      </div>
      {!enabled && (saved.subjectVendorIds.length > 0 || saved.subtypes.length > 0 || vendorIds.size > 0 || subtypes.size > 0) && (
        <p className="text-xs text-muted-foreground">Your vendor and kind choices are kept while this is off.</p>
      )}

      <Separator />

      <div role="group" aria-labelledby={`${uid}-kinds`} className="space-y-2">
        <div>
          <p id={`${uid}-kinds`} className="text-sm font-medium">Only these kinds</p>
          <p className="text-xs text-muted-foreground">Leave all unticked for every kind.</p>
        </div>
        <div className="flex flex-wrap gap-4">
          {SUBTYPES.map((e) => (
            <span key={e.slug} className="inline-flex items-center gap-2">
              <Checkbox
                id={`${uid}-kind-${e.slug}`}
                checked={subtypes.has(e.slug)}
                disabled={!ready}
                onCheckedChange={() => setSubtypes((s) => toggle(s, e.slug))}
              />
              <label htmlFor={`${uid}-kind-${e.slug}`} className="text-sm">
                {e.name}
              </label>
            </span>
          ))}
        </div>
      </div>

      <Separator />

      <div role="group" aria-labelledby={`${uid}-vendors`} className="space-y-2">
        <div>
          <p id={`${uid}-vendors`} className="text-sm font-medium">Only about these vendors</p>
          <p className="text-xs text-muted-foreground">
            Matches the vendor a story is about, not the site that published it. Leave empty for any vendor.
          </p>
        </div>
        {!savedVendors ? (
          <p className="text-xs italic text-muted-foreground">Loading vendors…</p>
        ) : (
          <>
            {dangling > 0 && (
              <p className="text-xs text-muted-foreground">
                {dangling === 1
                  ? '1 saved vendor is no longer listed. It will be dropped when you change and save your vendor choices.'
                  : `${dangling} saved vendors are no longer listed. They will be dropped when you change and save your vendor choices.`}
              </p>
            )}
            <Input
              type="search"
              aria-label="Filter vendors"
              placeholder="Search vendors…"
              maxLength={VENDOR_SEARCH_MAX_LENGTH}
              value={filter}
              onChange={(ev) => setFilter(ev.target.value)}
            />
            <ul className="max-h-56 space-y-1 overflow-y-auto" data-ph-no-capture>
              {listed.map((v, i) => {
                const blockedByLimit = atLimit && !vendorIds.has(v.id)
                return (
                  <li key={v.id} className="flex items-center gap-2">
                    {/* ids are useId + position: a vendor's UUID never reaches the DOM. */}
                    <Checkbox
                      id={`${uid}-vendor-${i}`}
                      checked={vendorIds.has(v.id)}
                      disabled={!ready || blockedByLimit}
                      aria-describedby={blockedByLimit ? limitId : undefined}
                      onCheckedChange={() => toggleVendor(v)}
                    />
                    <label htmlFor={`${uid}-vendor-${i}`} className="text-sm">
                      {v.name}
                    </label>
                  </li>
                )
              })}
            </ul>
            {/* Kept mounted so screen readers announce each change (codex on b8b1226f). */}
            <div role="status" aria-live="polite" aria-label="Vendor search" className="space-y-1">
              {atLimit && (
                <p id={limitId} className="text-xs text-muted-foreground">
                  You can choose up to {VENDOR_SUBJECT_MAX} vendors. Untick one to add another.
                </p>
              )}
              {search.searching && <p className="text-xs italic text-muted-foreground">Searching…</p>}
              {search.ready && filter.trim() !== '' && resultCount === 0 && (
                <p className="text-xs italic text-muted-foreground">No other vendors match that search.</p>
              )}
              {resultCount >= VENDOR_SEARCH_PAGE && (
                <p className="text-xs text-muted-foreground">
                  Showing the first {VENDOR_SEARCH_PAGE} matches. Type more of the name to narrow the list.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div role="alert">
                {search.failed && <p className="text-xs text-muted-foreground">Vendor search failed.</p>}
              </div>
              {search.failed && (
                <Button type="button" size="sm" variant="outline" onClick={search.retry}>
                  Try again
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          disabled={!ready || !dirty || save.isPending || !userId}
          onClick={() => save.mutate({ userId, vendorContent: changes })}
        >
          Save
        </Button>
        <p role="status" aria-live="polite" aria-label="Save result" className="text-xs text-muted-foreground">
          {outcome(save.error, save.isSuccess)}
        </p>
      </div>
    </div>
  )
}
